<template>
  <div class="flex flex-col gap-4 text-left" data-testid="professional-documents-page">
    <Tabs v-model="view" :tabs="tabs">
      <template #documents>
        <DocumentReviewSplitView />
      </template>
      <template #demands>
        <PreAllocationKanban @review-documents="reviewDocumentsOf" />
      </template>
    </Tabs>
  </div>
</template>

<script setup lang="ts">
import { computed, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import Tabs from '@/components/designSystem/Tabs/Tabs.vue'
import type { TabItem } from '@/components/designSystem/Tabs/Tabs.vue'
import { EMPTY_REVIEW_FILTERS, useDocumentReviewStore } from '@/pinia-store/useDocumentReviewStore'
import { usePreAllocationKanbanStore } from '@/pinia-store/usePreAllocationKanbanStore'
import type { PreAllocationCard } from '@/types/qualification-eligibility.types'

import DocumentReviewSplitView from './components/DocumentReviewSplitView.vue'
import PreAllocationKanban from './components/PreAllocationKanban.vue'
import { reviewSearchFor } from './model'

type View = 'documents' | 'demands'

const route = useRoute()
const router = useRouter()
const reviewStore = useDocumentReviewStore()
const kanbanStore = usePreAllocationKanbanStore()

/**
 * Aba na URL (`?view=demands`) para o link direto às Demandas urgentes. O resto da query fica:
 * com o hub desligado a página vive no shell de abas, que lê `?tab=`.
 */
const view = computed<View>({
  get: () => (route.query.view === 'demands' ? 'demands' : 'documents'),
  set: (next) => void router.replace({ query: { ...route.query, view: next } })
})

const tabs = computed<TabItem[]>(() => [
  { label: 'Documentos', value: 'documents', testId: 'professional-documents-tab-documents' },
  {
    // Contagem só depois da primeira carga do Kanban (sem request extra na aba Documentos).
    label: kanbanStore.isLoaded
      ? `Demandas urgentes (${kanbanStore.openCount})`
      : 'Demandas urgentes',
    value: 'demands',
    testId: 'professional-documents-tab-demands'
  }
])

/**
 * "Revisar documentos" no pop-up do Kanban: a aba Documentos abre filtrada pelo médico. Só
 * grava o filtro: a aba monta de novo ao ser escolhida e carrega a fila com ele (uma request).
 */
function reviewDocumentsOf(professional: PreAllocationCard['professional']) {
  reviewStore.$patch({
    filters: { ...EMPTY_REVIEW_FILTERS, search: reviewSearchFor(professional) }
  })
  view.value = 'documents'
}

onUnmounted(() => {
  reviewStore.$reset()
  kanbanStore.$reset()
})
</script>
